import React, { useEffect, useRef, useState } from 'react';
import { Chip, ControlIcon, IconButton, Paper } from './primitives';
import { Popper } from './overlays';
import { alpha, useTheme } from './theme';
import { TextField } from './inputs';
import { domProps, join, mergeRefs, useControlled, useRootStyles, useStableId } from './styles';
export function createFilterOptions({ ignoreAccents = true, ignoreCase = true, matchFrom = 'any', limit, stringify, trim = false } = {}) {
    return (options, { inputValue = '', getOptionLabel }) => {
        const convert = text => { let result = String(text ?? ''); if (trim)
            result = result.trim(); if (ignoreCase)
            result = result.toLowerCase(); if (ignoreAccents)
            result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); return result; };
        const query = convert(inputValue);
        const filtered = options.filter(option => { const text = convert((stringify || getOptionLabel)(option)); return matchFrom === 'start' ? text.startsWith(query) : text.includes(query); });
        return limit == null ? filtered : filtered.slice(0, limit);
    };
}
const defaultFilter = createFilterOptions();
export default function Autocomplete(props) {
    const { options = [], value: valueProp, defaultValue, inputValue: inputProp, onChange, onInputChange, getOptionLabel = option => option?.label ?? option, isOptionEqualToValue = (a, b) => a === b, getOptionDisabled = () => false, getOptionKey, filterOptions = defaultFilter, filterSelectedOptions, multiple, freeSolo, disabled, readOnly, size = 'medium', fullWidth, disableClearable, disableCloseOnSelect, autoHighlight, autoSelect, openOnFocus, clearOnBlur = !freeSolo, clearOnEscape, selectOnFocus = !freeSolo, blurOnSelect, limitTags = -1, renderTags, renderOption, renderInput, groupBy, renderGroup, loading, loadingText = 'Loading...', noOptionsText = 'No options', PaperComponent = Paper, PopperComponent = Popper, ListboxProps = {}, ListboxComponent = 'ul', componentsProps = {}, slotProps = {}, onFocus, onBlur, onKeyDown, onOpen, onClose, onHighlightChange, open: openProp, disablePortal, forcePopupIcon = 'auto', popupIcon, clearIcon, includeInputInList, handleHomeEndKeys = !freeSolo, disableListWrap, ...rest } = props;
    const getLabel = option => {
        if (option == null)
            return '';
        if (typeof option === 'string')
            return option;
        const result = getOptionLabel(option);
        return result == null ? '' : String(result);
    };
    const [value, setValue] = useControlled(valueProp, defaultValue ?? (multiple ? [] : null));
    const [inputValue, setInputValue] = useControlled(inputProp, !multiple ? getLabel(value) : '');
    const [open, setOpen] = useControlled(openProp, false);
    const [focused, setFocused] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const id = useStableId(props.id);
    const root = useRef();
    const input = useRef();
    const anchor = useRef();
    const listbox = useRef();
    const previousValue = useRef(value);
    const theme = useTheme();
    const paperCss = useRootStyles('AutocompletePaper', {}, { ...theme.typography.body1, '--ui-option-selected': alpha(theme.palette.primary.main, 0.08), '--ui-option-selected-hover': alpha(theme.palette.primary.main, 0.12) });
    const css = useRootStyles('Autocomplete', rest, fullWidth ? { width: '100%' } : {});
    const selected = multiple ? Array.isArray(value) ? value : [] : value == null ? [] : [value];
    const isSelected = option => selected.some(v => isOptionEqualToValue(option, v));
    const safeOptions = Array.isArray(options) ? options : [];
    const filtered = filterOptions(safeOptions.filter(o => !filterSelectedOptions || !isSelected(o)), { inputValue: !multiple && value != null && inputValue === getLabel(value) ? '' : inputValue || '', getOptionLabel: getLabel }) || [];
    const hasPopup = !!open && !disabled && !readOnly;
    const setInput = (event, text, reason) => { const next = text ?? ''; if (next !== inputValue) {
        setInputValue(next);
        onInputChange?.(event, next, reason);
    } };
    const show = event => { if (!open && !disabled && !readOnly) {
        setOpen(true);
        onOpen?.(event);
        setHighlight(autoHighlight ? filtered.findIndex(o => !getOptionDisabled(o)) : -1);
    } };
    const hide = (event, reason) => { if (open) {
        setOpen(false);
        onClose?.(event, reason);
        setHighlight(-1);
    } };
    useEffect(() => {
        if (previousValue.current !== value) {
            previousValue.current = value;
            setInput(null, multiple ? '' : getLabel(value), 'reset');
        }
    }, [value]);
    useEffect(() => { if (highlight >= filtered.length)
        setHighlight(autoHighlight ? filtered.findIndex(o => !getOptionDisabled(o)) : -1); }, [filtered.length, highlight, autoHighlight]);
    useEffect(() => { if (hasPopup && highlight >= 0)
        document.getElementById(`${id}-option-${highlight}`)?.scrollIntoView?.({ block: 'nearest' }); }, [highlight, hasPopup, id]);
    const change = (event, next, reason, details) => { setValue(next); onChange?.(event, next, reason, details); };
    const remove = (event, option) => { const next = selected.filter(v => !isOptionEqualToValue(option, v)); change(event, next, 'removeOption', { option }); input.current?.focus(); };
    const choose = (event, option, reason = 'selectOption') => {
        if (option == null || getOptionDisabled(option) || readOnly)
            return;
        let next = option;
        let actualReason = reason;
        if (multiple) {
            if (isSelected(option)) {
                next = selected.filter(v => !isOptionEqualToValue(option, v));
                actualReason = 'removeOption';
            }
            else
                next = [...selected, option];
        }
        setInput(event, multiple ? '' : getLabel(option), 'reset');
        change(event, next, actualReason, { option });
        if (!disableCloseOnSelect && !event.ctrlKey && !event.metaKey)
            hide(event, 'selectOption');
        if (blurOnSelect === true || blurOnSelect === 'mouse' && event.type === 'click' || blurOnSelect === 'touch' && event.type?.startsWith('touch'))
            input.current?.blur();
        else
            input.current?.focus();
    };
    const clear = event => { if (readOnly || disabled)
        return; setInput(event, '', 'clear'); change(event, multiple ? [] : null, 'clear'); input.current?.focus(); };
    const move = (event, target) => {
        if (!filtered.length)
            return;
        let next = target;
        const direction = target < highlight ? -1 : 1;
        for (let attempts = 0; attempts < filtered.length; attempts++) {
            if (disableListWrap)
                next = Math.max(includeInputInList ? -1 : 0, Math.min(next, filtered.length - 1));
            else if (next < (includeInputInList ? -1 : 0))
                next = filtered.length - 1;
            else if (next >= filtered.length)
                next = includeInputInList ? -1 : 0;
            if (next === -1 || !getOptionDisabled(filtered[next]))
                break;
            next += direction;
        }
        setHighlight(next);
        onHighlightChange?.(event, next < 0 ? null : filtered[next], 'keyboard');
    };
    const keyboard = event => {
        onKeyDown?.(event);
        if (event.defaultPrevented || event.defaultUiPrevented || event.isComposing || event.nativeEvent?.isComposing)
            return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            show(event);
            move(event, highlight < 0 ? event.key === 'ArrowDown' ? 0 : filtered.length - 1 : highlight + (event.key === 'ArrowDown' ? 1 : -1));
        }
        else if (handleHomeEndKeys && hasPopup && ['Home', 'End'].includes(event.key)) {
            event.preventDefault();
            move(event, event.key === 'Home' ? 0 : filtered.length - 1);
        }
        else if (event.key === 'PageDown' && hasPopup) {
            event.preventDefault();
            move(event, highlight + 5);
        }
        else if (event.key === 'PageUp' && hasPopup) {
            event.preventDefault();
            move(event, highlight - 5);
        }
        else if (event.key === 'Enter') {
            if (hasPopup && highlight >= 0 && filtered[highlight]) {
                event.preventDefault();
                choose(event, filtered[highlight]);
            }
            else if (freeSolo && String(inputValue || '').trim()) {
                event.preventDefault();
                choose(event, inputValue, 'createOption');
            }
        }
        else if (event.key === 'Escape') {
            if (hasPopup) {
                event.preventDefault();
                event.stopPropagation();
                hide(event, 'escape');
            }
            else if (clearOnEscape)
                clear(event);
        }
        else if (event.key === 'Backspace' && multiple && !inputValue && selected.length && !readOnly) {
            remove(event, selected[selected.length - 1]);
        }
    };
    const chipProps = index => ({ key: getOptionKey?.(selected[index]) ?? index, className: 'UiAutocomplete-tag', size, disabled, onDelete: readOnly ? undefined : event => remove(event, selected[index]), tabIndex: -1, 'data-tag-index': index });
    const tags = multiple && selected.length ? renderTags ? renderTags(selected, ({ index }) => chipProps(index), props) : <>{selected.slice(0, !focused && limitTags >= 0 ? limitTags : undefined).map((option, i) => <Chip {...chipProps(i)} label={getLabel(option)}/>)}{!focused && limitTags >= 0 && selected.length > limitTags && <span className="UiAutocomplete-tag">+{selected.length - limitTags}</span>}</> : undefined;
    const hasClear = !disableClearable && !disabled && !readOnly && (selected.length > 0 || !!inputValue);
    const hasArrow = forcePopupIcon === true || forcePopupIcon !== false && !freeSolo;
    const endAdornment = (hasClear || hasArrow) && <div className="UiAutocomplete-endAdornment">{hasClear && <IconButton {...componentsProps.clearIndicator} {...slotProps.clearIndicator} size="small" className="UiAutocomplete-clearIndicator" tabIndex={-1} aria-label={props.clearText || 'Clear'} title={props.clearText || 'Clear'} onMouseDown={e => e.preventDefault()} onClick={clear}>{clearIcon || <ControlIcon kind="close" fontSize="small"/>}</IconButton>}{hasArrow && <IconButton {...componentsProps.popupIndicator} {...slotProps.popupIndicator} size="small" className={join('UiAutocomplete-popupIndicator', hasPopup && 'UiAutocomplete-popupIndicatorOpen')} tabIndex={-1} disabled={disabled} aria-label={hasPopup ? props.closeText || 'Close' : props.openText || 'Open'} onMouseDown={e => e.preventDefault()} onClick={e => { if (hasPopup)
        hide(e, 'toggleInput');
    else {
        input.current?.focus();
        show(e);
    } }}>{popupIcon || <ControlIcon kind="down"/>}</IconButton>}</div>;
    const params = {
        id, disabled, fullWidth: true, size,
        InputLabelProps: { id: `${id}-label`, htmlFor: id },
        InputProps: { ref: anchor, className: 'UiAutocomplete-inputRoot', startAdornment: tags, endAdornment },
        inputProps: { ref: input, id, value: inputValue ?? '', role: 'combobox', autoComplete: 'off', spellCheck: false, disabled, readOnly, className: 'UiAutocomplete-input', 'aria-autocomplete': freeSolo ? 'both' : 'list', 'aria-expanded': hasPopup, 'aria-controls': hasPopup ? `${id}-listbox` : undefined, 'aria-activedescendant': hasPopup && highlight >= 0 ? `${id}-option-${highlight}` : undefined,
            onChange: e => { setInput(e, e.target.value, 'input'); setHighlight(-1); if (e.target.value === '' && !multiple && !disableClearable && value !== null)
                change(e, null, 'clear'); show(e); },
            onFocus: e => { setFocused(true); if (selectOnFocus)
                e.target.select?.(); if (openOnFocus)
                show(e); onFocus?.(e); },
            onBlur: e => { if (listbox.current?.contains(e.relatedTarget))
                return; setFocused(false); if (autoSelect && hasPopup && highlight >= 0)
                choose(e, filtered[highlight], 'blur');
            else if (autoSelect && freeSolo && inputValue)
                choose(e, inputValue, 'blur');
            else if (clearOnBlur)
                setInput(e, multiple ? '' : getLabel(value), 'blur'); hide(e, 'blur'); onBlur?.(e); },
            onMouseDown: e => { if (!hasPopup)
                show(e); },
        },
    };
    const renderItem = (option, index) => {
        const optionProps = { key: getOptionKey?.(option) ?? `${getLabel(option)}-${index}`, id: `${id}-option-${index}`, role: 'option', tabIndex: -1, 'data-option-index': index, 'aria-selected': isSelected(option), 'aria-disabled': getOptionDisabled(option) || undefined, className: join('UiAutocomplete-option', index === highlight && 'Ui-focused', isSelected(option) && 'Ui-selected'), onMouseMove: e => { if (!getOptionDisabled(option) && highlight !== index) {
                setHighlight(index);
                onHighlightChange?.(e, option, 'mouse');
            } }, onMouseDown: e => e.preventDefault(), onClick: e => choose(e, option) };
        return renderOption ? renderOption(optionProps, option, { selected: isSelected(option), inputValue, index }, props) : <li {...optionProps}>{getLabel(option)}</li>;
    };
    let optionNodes = filtered.map(renderItem);
    if (groupBy) {
        const groups = [];
        filtered.forEach((option, index) => { const group = groupBy(option); let current = groups[groups.length - 1]; if (!current || current.group !== group) {
            current = { group, key: index, options: [] };
            groups.push(current);
        } current.options.push(renderItem(option, index)); });
        optionNodes = groups.map(g => renderGroup ? renderGroup({ key: g.key, group: g.group, children: g.options }) : <li key={g.key}><div className="UiAutocomplete-groupLabel">{g.group}</div><ul className="UiAutocomplete-groupUl">{g.options}</ul></li>);
    }
    const Listbox = ListboxComponent;
    return <div {...domProps(rest, ['id', 'clearText', 'closeText', 'openText', 'autoComplete', 'disableListWrap', 'ChipProps'])} ref={root} className={join('UiAutocomplete-root', focused && 'Ui-focused', multiple && 'UiAutocomplete-multiple', size === 'small' && 'UiAutocomplete-sizeSmall', hasClear && 'UiAutocomplete-hasClearIcon', hasArrow && 'UiAutocomplete-hasPopupIcon', css, props.className)} onKeyDown={keyboard}>{renderInput ? renderInput(params) : <TextField {...params}/>}<PopperComponent {...componentsProps.popper} {...slotProps.popper} open={hasPopup} anchorEl={anchor.current || root.current} disablePortal={disablePortal} placement="bottom-start" className="UiAutocomplete-popper" style={{ width: root.current?.getBoundingClientRect().width, zIndex: 1300, ...componentsProps.popper?.style, ...slotProps.popper?.style }}><PaperComponent {...componentsProps.paper} {...slotProps.paper} className={join('UiAutocomplete-paper', paperCss, componentsProps.paper?.className, slotProps.paper?.className)} elevation={1}>{filtered.length ? <Listbox {...ListboxProps} ref={mergeRefs(listbox, ListboxProps.ref)} id={`${id}-listbox`} role="listbox" aria-labelledby={`${id}-label`} aria-multiselectable={multiple || undefined} className={join('UiAutocomplete-listbox', ListboxProps.className)} onMouseDown={e => e.preventDefault()}>{optionNodes}</Listbox> : <div className="UiAutocomplete-noOptions" role="presentation" onMouseDown={e => e.preventDefault()}>{loading ? loadingText : noOptionsText}</div>}</PaperComponent></PopperComponent></div>;
}
